import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ErrorBoundary,
  SportradarFactory,
  ContentAndSidebar,
  buildSportradarList,
  MatchNav,
  MEDIA_BREAKPOINTS,
  MEDIUM_SCREEN_SIZE,
  SMALL_SCREEN_SIZE,
  LARGE_SCREEN_SIZE,
  configureMatchColumnLength,
} from 'mm-ui-components';
import { Store } from 'redux';
import { StyleSheet, css } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SLOT_NAMES, SPORTS } from '../templates.const';
import { Page } from '../Page';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
} from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getSportradarProps,
} from '../../store/template/widgetPage/widgetPagePhilly/widgetPagePhilly.selectors';
import { AdditionalStyleSheet } from '../templates.utils';

interface WidgetPagePhillyProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const WidgetPagePhillyComponent: React.FunctionComponent<
  WidgetPagePhillyProps
> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <WidgetPagePhillyContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
    widgetStyle: {
      marginTop: '0',
    },
    widgetWrapper: {
      margin: '0',
      padding: '0',
      boxShadow: 'none',
    },
    adContainer: {
      backgroundColor: 'white',
      boxShadow: '0 0 3px rgba(0,0,0,.2)',
      marginBottom: '20px',
      padding: '20px 20px',
    },
    sectionWrapper: {
      maxWidth: `${LARGE_SCREEN_SIZE}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        maxWidth: `${MEDIUM_SCREEN_SIZE}px`,
        padding: '0 20px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        maxWidth: `${SMALL_SCREEN_SIZE}px`,
        padding: '0',
      },
      display: 'flex',
      flexDirection: 'column',
      justifyItems: 'left',
      margin: '0 auto 10px',
    },
  });

const widgetContainer = (showSecondColumn: boolean) => StyleSheet.create({
    style: {
      margin: '20px auto 40px',
      columnGap: '20px',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: showSecondColumn ? '2fr 1fr' : '1fr',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        margin: '0 20px',
        width: 'unset',
      },
      [MEDIA_BREAKPOINTS.small]: {
        margin: '0 20px',
        width: 'unset',
        display: 'flex',
        flexDirection: 'column',
      },
    },
  }) as AdditionalStyleSheet;

const sideBarAdStyle = StyleSheet.create({
  style: {
    minHeight: '250px !important',
  },
}) as AdditionalStyleSheet;

export const WidgetPagePhillyContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const metadataDescription = useSelector(getMetadataDescription);
  const metadataTitle = useSelector(getMetadataTitle);
  const metadataImage = useSelector(getMetadataImage);
  const sidebar1Ad = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar1AdOnSuccess = useSelector(
    getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1),
  );
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const canonical = useSelector(getCanonicalUrl);
  const sportradarProps = useSelector(getSportradarProps);
  const sportradarWidgets = buildSportradarList({ ...sportradarProps });
  const widgetListLength = sportradarWidgets?.length;
  const { tab, matchStatus, league } = sportradarProps;
  const columnLength = configureMatchColumnLength(widgetListLength, tab, matchStatus, league);

  const showSecondColumn = () => {
    if (league === SPORTS.SOCCER && tab === SPORTS.BETTING) {
      return false;
    }

    return (
      widgetListLength >= 4
      || ((league === SPORTS.NHL || league === SPORTS.NBA || league === SPORTS.NFL)
        && tab === SPORTS.BETTING)
    );
  };
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags
        title={metadataTitle}
        description={metadataDescription}
        pageImage={metadataImage}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout
            mainWrapperConfigThemeKey="mainContent"
            isFullBleed
            noMarginTop
          >
            <div className={css(style.sectionWrapper)}>
              <SportradarFactory
                sportradarList={sportradarWidgets?.slice(0, 1)}
                wrapperStyle={style.widgetWrapper}
                widgetStyle={style.widgetStyle}
              />
              <MatchNav
                sportradarProps={sportradarProps}
                canonicalURL={canonical}
              />
            </div>
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
            <ContentAndSidebar
              additionalStyles={widgetContainer(showSecondColumn())}
            >
              <div>
                {showSecondColumn() ? (
                  <SportradarFactory
                    sportradarList={sportradarWidgets?.slice(1, columnLength)}
                    widgetStyle={style.widgetStyle}
                  />
                ) : (
                  <div>
                    <SportradarFactory
                      sportradarList={sportradarWidgets?.slice(1, 2)}
                      widgetStyle={style.widgetStyle}
                    />
                    <div className={css(style.adContainer)}>
                      <Ad
                        id={sidebar1Ad}
                        onSuccess={sideBar1AdOnSuccess}
                        setMinHeight
                        additionalStyle={sideBarAdStyle}
                      />
                    </div>
                    <SportradarFactory
                      sportradarList={sportradarWidgets?.slice(
                        2,
                        columnLength + 2,
                      )}
                      widgetStyle={style.widgetStyle}
                    />
                  </div>
                )}
              </div>
              {showSecondColumn() && (
                <aside>
                  <SportradarFactory
                    sportradarList={sportradarWidgets?.slice(
                      columnLength,
                      columnLength + 1,
                    )}
                    widgetStyle={style.widgetStyle}
                  />
                  <div className={css(style.adContainer)}>
                    <Ad
                      id={sidebar1Ad}
                      onSuccess={sideBar1AdOnSuccess}
                      setMinHeight
                      additionalStyle={sideBarAdStyle}
                    />
                  </div>
                  <SportradarFactory
                    sportradarList={sportradarWidgets?.slice(columnLength + 1)}
                    widgetStyle={style.widgetStyle}
                  />
                </aside>
              )}
            </ContentAndSidebar>
          </NavigationLayout>
          <div id="bottom-placeholder" />
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
